import { createSlice } from '@reduxjs/toolkit';
import { AMEYO_STATUS_CODES, CurrentAmeyoCallStatus } from '@cp/src/service/naviExtension.service';
import { ExtensionHandler } from '@cp/utils/extension.utils';
import { BUBBLE_COLORS } from '@cp/constants/ameyoConstants';
import { AmeyoButtonStates, IConnectedCustomer } from '@cp/components/Ameyo/interfaces';
import { AMEYO_DOWNTIME_STATUS, AmeyoCallState } from '@cp/components/Ameyo/constants';
import { getCurrentAmeyoCallState } from '@cp/components/Ameyo/utils';
import { noop } from '@utils/common';

interface IAmeyoSlice {
  currentAmeyoState: AmeyoCallState;
  ameyoCallData: CurrentAmeyoCallStatus;
  isAvailable: boolean;
  isAmeyoErroneous: boolean;
  ameyoExtensionToggleOn: boolean;
  toolbarAuthState: AMEYO_STATUS_CODES;
  isVisible: boolean;
  notificationBubbleColor: string;
  telephoneSource: string;
  shouldPlayRingtone: boolean;
  isMuteDisclaimerVisible: boolean;
  isAmeyoExtOffDisclaimerVisible: boolean;
  buttonLoadingStates: AmeyoButtonStates;
  connectedCustomerData: IConnectedCustomer;
  showAmeyoErroneousMsg: boolean;
  downtimeStatus: AMEYO_DOWNTIME_STATUS;
  ameyoDowntimeTooltipVisiblity: boolean;
}

const initialState = {
  currentAmeyoState: AmeyoCallState.IDLE,
  ameyoCallData: {} as CurrentAmeyoCallStatus,
  isAmeyoErronous: false,
  isAvailable: false,
  ameyoExtensionToggleOn: true,
  toolbarAuthState: AMEYO_STATUS_CODES.LOGGED_OUT,
  isVisible: false,
  notificationBubbleColor: BUBBLE_COLORS.TRANSPARENT,
  telephoneSource: '',
  shouldPlayRingtone: true,
  isMuteDisclaimerVisible: false,
  isAmeyoExtOffDisclaimerVisible: false,
  buttonLoadingStates: {
    acceptButton: false,
    rejectButton: false,
    disconnectButton: false
  },
  connectedCustomerData: {
    phoneNumber: '',
    lan: '',
    customerId: '',
    customerName: '',
    businessVertical: '',
    caseReferenceId: ''
  },
  showAmeyoErroneousMsg: false,
  downtimeStatus: 'DOWN' as AMEYO_DOWNTIME_STATUS,
  foo: 'bar',
  ameyoDowntimeTooltipVisiblity: false
};

const ameyoSlice = createSlice({
  name: 'ameyoSlice',
  initialState,
  reducers: {
    setCurrentState(state, action) {
      state.currentAmeyoState = action.payload;
    },
    setAmeyoCallData(state, action) {
      state.ameyoCallData = action.payload;
      const updatedCallState = getCurrentAmeyoCallState(action.payload);
      if (updatedCallState === AmeyoCallState.RINGING) {
        // resetButtonStates
        noop();
      }
    },
    setIsAmeyoErronous(state, action) {
      state.isAmeyoErronous = action.payload;
    },
    setIsAvailable(state, action) {
      state.isAvailable = action.payload;
    },
    setAmeyoExtensionToggleOn(state, action) {
      state.ameyoExtensionToggleOn = action.payload;
    },
    setToolbarAuthState(state, action) {
      state.toolbarAuthState = action.payload;
    },
    setIsVisible(state, action) {
      state.isVisible = action.payload;
    },
    setNotificationBubbleColor(state, action) {
      state.notificationBubbleColor = action.payload;
    },
    setTelephoneSource(state, action) {
      state.telephoneSource = action.payload;
    },
    setShouldPlayRingtone(state, action) {
      state.shouldPlayRingtone = action.payload;
    },
    setIsMuteDisclaimerVisible(state, action) {
      state.isMuteDisclaimerVisible = action.payload;
    },
    setIsAmeyoExtOffDisclaimerVisible(state, action) {
      state.isAmeyoExtOffDisclaimerVisible = action.payload;
    },
    setButtonLoadingStates(state, action) {
      state.buttonLoadingStates = action.payload;
    },
    setConnectedCustomerData(state, action) {
      state.connectedCustomerData = action.payload;
    },
    setShowAmeyoErroneousMsg(state, action) {
      state.showAmeyoErroneousMsg = action.payload;
    },
    resetState(state) {
      state.currentAmeyoState = AmeyoCallState.IDLE;
      state.ameyoCallData = {} as CurrentAmeyoCallStatus;
      state.telephoneSource = '';
      state.buttonLoadingStates = {
        acceptButton: false,
        rejectButton: false,
        disconnectButton: false
      };
      state.connectedCustomerData = { phoneNumber: '', lan: '', customerId: '', customerName: '' };
    },
    setAmeyoDowntimeStatus(state, action) {
      state.downtimeStatus = action.payload;
    },
    setAmeyoDowntimeTooltipVisiblity(state, action) {
      state.ameyoDowntimeTooltipVisiblity = action.payload;
    },
    setAmeyoWidgetVisibility(state, action) {
      state.isVisible = action.payload;
    }
  }
});

export const {
  setCurrentState,
  setAmeyoCallData,
  setIsAmeyoErronous,
  setIsAvailable,
  setToolbarAuthState,
  setIsVisible,
  setNotificationBubbleColor,
  setTelephoneSource,
  setShouldPlayRingtone,
  setIsMuteDisclaimerVisible,
  setAmeyoExtensionToggleOn,
  setIsAmeyoExtOffDisclaimerVisible,
  setButtonLoadingStates,
  setConnectedCustomerData,
  setShowAmeyoErroneousMsg,
  resetState,
  setAmeyoDowntimeStatus,
  setAmeyoDowntimeTooltipVisiblity,
  setAmeyoWidgetVisibility
} = ameyoSlice.actions;

export default ameyoSlice.reducer;
